/**
 * Pages List Tab Component
 *
 * Displays list of parsed URLs (pages with paths) for a project
 */

import { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import {
  Table,
  Button,
  Space,
  Tag,
  Typography,
  message,
  Card,
  Empty,
  Modal,
  Progress,
} from 'antd'
import {
  ReloadOutlined,
  LinkOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExperimentOutlined,
  PhoneOutlined,
} from '@ant-design/icons'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table/interface'
import api from '@/services/api'

const { Text } = Typography

interface PagesListTabProps {
  projectId: number
}

interface ParsedUrl {
  id: number
  project_id: number
  parsing_task_id: string | null
  full_url: string
  domain: string
  path: string | null
  total_clicks: number
  queries_count: number
  average_position: number
  popularity: string
  source_status: string
  review_status: string | null  // * Статус проверки в swipe mode
  selected_for_upload: boolean
  ai_analysis_status: string | null
  ai_recommendation: string | null
  ai_score: number | null
  ai_reason: string | null
  created_at: string
  updated_at: string
}

const statusColors: Record<string, string> = {
  pending: 'warning',
  selected: 'processing',
  uploaded: 'success',
  approved: 'success',
  rejected: 'error',
}

const statusLabels: Record<string, string> = {
  pending: 'Ожидание',
  selected: 'Выбран',
  uploaded: 'Загружен',
  approved: 'Одобрен',
  rejected: 'Отклонён',
}

const aiStatusColors: Record<string, string> = {
  not_started: 'default',
  pending: 'warning',
  analyzing: 'processing',
  completed: 'success',
  failed: 'error',
  skipped: 'default',
}

const aiStatusLabels: Record<string, string> = {
  not_started: 'Не начат',
  pending: 'В очереди',
  analyzing: 'Анализ...',
  completed: 'Завершён',
  failed: 'Ошибка',
  skipped: 'Пропущен',
}

const aiRecommendationColors: Record<string, string> = {
  approved: 'success',
  rejected: 'error',
  uncertain: 'warning',
}

const aiRecommendationLabels: Record<string, string> = {
  approved: 'Одобрено',
  rejected: 'Отклонено',
  uncertain: 'Неопределённо',
}

const PagesListTab = forwardRef(({ projectId }: PagesListTabProps, ref) => {
  const [pages, setPages] = useState<ParsedUrl[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  })

  // Phone extraction state
  const [extracting, setExtracting] = useState(false)
  const [extractionProgress, setExtractionProgress] = useState<{
    progress: number
    current_step: string
    total_urls: number
    processed_urls: number
    total_phones_extracted: number
  } | null>(null)
  const [extractionModalVisible, setExtractionModalVisible] = useState(false)

  const fetchPages = async (page: number = 1, pageSize: number = 20) => {
    if (!projectId) return

    setLoading(true)

    try {
      const response = await api.getParsedUrls({
        project_id: projectId,
        has_path: true, // Server-side filter for pages with paths
        page,
        page_size: pageSize,
      })

      setPages(response.items || [])
      setPagination({
        current: response.page || page,
        pageSize: response.page_size || pageSize,
        total: response.total || 0,
      })
    } catch (error) {
      console.error('Fetch pages error:', error)
      message.error('Ошибка загрузки страниц')
    } finally {
      setLoading(false)
    }
  }

  useImperativeHandle(ref, () => ({
    refresh: () => fetchPages(pagination.current, pagination.pageSize),
  }))

  useEffect(() => {
    fetchPages()
  }, [projectId])

  const handleTableChange = (newPagination: TablePaginationConfig) => {
    setPagination({
      ...pagination,
      current: newPagination.current || 1,
      pageSize: newPagination.pageSize || pagination.pageSize,
    })
    fetchPages(newPagination.current || 1, newPagination.pageSize || pagination.pageSize)
  }

  // Get approved pages count
  const approvedPagesCount = pages.filter(p => p.review_status === 'approved').length

  // Start phone extraction for approved pages
  const handleStartExtraction = async () => {
    const approvedPages = pages.filter(p => p.review_status === 'approved')

    if (approvedPages.length === 0) {
      message.warning('Нет одобренных страниц для сбора телефонов')
      return
    }

    setExtracting(true)
    setExtractionModalVisible(true)
    setExtractionProgress(null)

    try {
      const result = await api.startBatchPhoneExtraction({
        project_id: projectId,
        parsed_url_ids: approvedPages.map(p => p.id),
        methods: ['quick', 'phone_parser'],
      })

      message.success(`Запущен сбор телефонов для ${approvedPages.length} страниц`)

      // Start polling for progress
      pollExtractionProgress(result.task_id)
    } catch (error) {
      console.error('Start extraction error:', error)
      message.error('Ошибка запуска сбора телефонов')
      setExtracting(false)
      setExtractionModalVisible(false)
    }
  }

  // Poll extraction task progress
  const pollExtractionProgress = async (taskId: number) => {
    try {
      const status = await api.getPhoneExtractionTaskStatus(taskId)

      setExtractionProgress({
        progress: status.progress || 0,
        current_step: status.current_step || 'Обработка...',
        total_urls: status.total_urls || 0,
        processed_urls: status.processed_urls || 0,
        total_phones_extracted: status.total_phones_extracted || 0,
      })

      if (status.status === 'completed') {
        message.success(`Сбор завершён! Найдено ${status.total_phones_extracted} телефонов`)
        setExtracting(false)
        // Refresh the pages list
        fetchPages(pagination.current, pagination.pageSize)
      } else if (status.status === 'failed') {
        message.error(`Ошибка: ${status.error_message || 'Неизвестная ошибка'}`)
        setExtracting(false)
      } else {
        // Continue polling
        setTimeout(() => pollExtractionProgress(taskId), 2000)
      }
    } catch (error) {
      console.error('Poll extraction progress error:', error)
      // Retry polling
      setTimeout(() => pollExtractionProgress(taskId), 3000)
    }
  }

  const columns: ColumnsType<ParsedUrl> = [
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>URL</span>,
      dataIndex: 'full_url',
      key: 'full_url',
      ellipsis: true,
      render: (url: string) => (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: '13px', color: '#722ed1' }}
        >
          <LinkOutlined style={{ marginRight: 4 }} />
          {url}
        </a>
      ),
    },
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>Домен</span>,
      dataIndex: 'domain',
      key: 'domain',
      width: 180,
      ellipsis: true,
      render: (domain: string) => (
        <Text style={{ fontSize: '13px', color: '#595959' }}>{domain}</Text>
      ),
    },
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>Путь</span>,
      dataIndex: 'path',
      key: 'path',
      width: 200,
      ellipsis: true,
      render: (path: string | null) => (
        path ? (
          <Text code style={{ fontSize: '11px' }}>{path}</Text>
        ) : (
          <Text type="secondary" style={{ fontSize: '12px' }}>—</Text>
        )
      ),
    },
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>Статус</span>,
      key: 'status',
      width: 120,
      render: (_: any, record: ParsedUrl) => {
        // * Показываем review_status если есть (результат проверки в swipe mode)
        const status = record.review_status || record.source_status
        return (
          <Tag
            color={statusColors[status] || 'default'}
            icon={status === 'approved' ? <CheckCircleOutlined /> : status === 'rejected' ? <CloseCircleOutlined /> : status === 'pending' ? <ClockCircleOutlined /> : undefined}
            style={{ margin: 0, fontSize: '11px' }}
          >
            {statusLabels[status] || status}
          </Tag>
        )
      },
    },
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>AI Анализ</span>,
      key: 'ai_analysis',
      width: 150,
      render: (_: any, record: ParsedUrl) => {
        if (!record.ai_analysis_status || record.ai_analysis_status === 'not_started') {
          return <Text type="secondary" style={{ fontSize: '12px' }}>—</Text>
        }

        return (
          <Space direction="vertical" size={2}>
            <Tag
              color={aiStatusColors[record.ai_analysis_status] || 'default'}
              icon={record.ai_analysis_status === 'analyzing' ? <ExperimentOutlined spin /> : undefined}
              style={{ margin: 0, fontSize: '10px' }}
            >
              {aiStatusLabels[record.ai_analysis_status] || record.ai_analysis_status}
            </Tag>
            {record.ai_recommendation && (
              <Tag
                color={aiRecommendationColors[record.ai_recommendation] || 'default'}
                style={{ margin: 0, fontSize: '10px' }}
              >
                {aiRecommendationLabels[record.ai_recommendation] || record.ai_recommendation}
                {record.ai_score !== null && ` (${(record.ai_score * 100).toFixed(0)}%)`}
              </Tag>
            )}
          </Space>
        )
      },
    },
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>Клики</span>,
      dataIndex: 'total_clicks',
      key: 'total_clicks',
      width: 80,
      align: 'right' as const,
      render: (clicks: number) => (
        <Text style={{ fontSize: '13px' }}>{clicks || 0}</Text>
      ),
    },
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>Позиция</span>,
      dataIndex: 'average_position',
      key: 'average_position',
      width: 80,
      align: 'right' as const,
      render: (position: number) => (
        position ? (
          <Text style={{ fontSize: '13px' }}>{position.toFixed(1)}</Text>
        ) : (
          <Text type="secondary" style={{ fontSize: '12px' }}>—</Text>
        )
      ),
    },
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>Источник</span>,
      dataIndex: 'popularity',
      key: 'popularity',
      width: 100,
      render: (popularity: string) => (
        <Tag
          color={popularity === 'manual' ? 'purple' : 'default'}
          style={{ margin: 0, fontSize: '10px' }}
        >
          {popularity === 'manual' ? 'Ручной' : popularity || 'Парсинг'}
        </Tag>
      ),
    },
  ]

  return (
    <div>
      {/* Header */}
      <Card
        style={{
          marginBottom: 16,
          borderRadius: 6,
          border: '1px solid #e8e8e8',
        }}
        bodyStyle={{ padding: '12px 20px' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <LinkOutlined style={{ color: '#722ed1', fontSize: '16px' }} />
            <Text strong style={{ fontSize: '14px' }}>Страницы (URL с путями)</Text>
            <Tag color="purple">{pagination.total}</Tag>
            {approvedPagesCount > 0 && (
              <Tag color="success">{approvedPagesCount} одобрено</Tag>
            )}
          </Space>
          <Space>
            <Button
              size="small"
              type="primary"
              icon={<PhoneOutlined />}
              onClick={handleStartExtraction}
              loading={extracting}
              disabled={approvedPagesCount === 0}
            >
              Собрать телефоны
            </Button>
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => fetchPages(pagination.current, pagination.pageSize)}
              loading={loading}
            >
              Обновить
            </Button>
          </Space>
        </div>
      </Card>

      {/* Table */}
      <Card
        style={{
          borderRadius: 6,
          border: '1px solid #e8e8e8',
        }}
        bodyStyle={{ padding: 0 }}
      >
        {pages.length === 0 && !loading ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Нет страниц для отображения"
            style={{ padding: '40px 0' }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={pages}
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total: number) => `Всего: ${total}`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            rowKey="id"
            size="small"
          />
        )}
      </Card>

      {/* Phone Extraction Progress Modal */}
      <Modal
        title="Сбор телефонов"
        open={extractionModalVisible}
        onCancel={() => {
          if (!extracting) {
            setExtractionModalVisible(false)
            setExtractionProgress(null)
          }
        }}
        footer={
          extracting ? null : (
            <Button type="primary" onClick={() => setExtractionModalVisible(false)}>
              Закрыть
            </Button>
          )
        }
        closable={!extracting}
        maskClosable={!extracting}
      >
        {extractionProgress ? (
          <div style={{ textAlign: 'center' }}>
            <Progress
              type="circle"
              percent={extractionProgress.progress}
              status={extracting ? 'active' : 'success'}
            />
            <div style={{ marginTop: 16 }}>
              <Text strong>{extractionProgress.current_step}</Text>
            </div>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">
                Обработано: {extractionProgress.processed_urls} / {extractionProgress.total_urls} страниц
              </Text>
            </div>
            <div style={{ marginTop: 8 }}>
              <Text>
                <PhoneOutlined style={{ color: '#52c41a', marginRight: 4 }} />
                Найдено телефонов: <Text strong>{extractionProgress.total_phones_extracted}</Text>
              </Text>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <Text>Запуск сбора телефонов...</Text>
          </div>
        )}
      </Modal>
    </div>
  )
})

PagesListTab.displayName = 'PagesListTab'

export default PagesListTab
